/**
 * /chat — widget de atendimento PÚBLICO (simulado): página limpa fora do
 * AppLayout/auth, card central estilo widget. Sem token salvo → formulário
 * Nome (obrigatório) + Telefone (POST /public/chat/start); com token → thread
 * com polling 3s (mensagens visíveis), composer e, encerrado, avaliação 1–5.
 *
 * v11: o widget lê GET /public/chat/config antes de abrir conversa —
 * respeita `enabled` (desligado ⇒ bloqueia INICIAR; um token salvo segue
 * atendido), aplica `title` no header e a logo da organização (best-effort:
 * config indisponível ⇒ comportamento de sempre). O `greeting` chega como
 * mensagem de sistema na thread — entrega única, sem duplicar no formulário.
 *
 * Tenant: as rotas públicas SEM recurso na URL (POST /public/chat e GET
 * /public/chat/config) exigem `X-Tenant` ou `?tenant=<slug>` (ver
 * publicTenantMiddleware). A página é aberta como /chat?tenant=<slug> e o
 * widget repassa esse mesmo seletor nas duas chamadas.
 */
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircleOff, Paperclip, SendHorizonal } from 'lucide-react';
import { api, apiUrl, type ApiError } from '@/lib/api-client';
import type { ChatMessage, PublicChatState, PublicWidgetConfig } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { PublicBrandMark } from '@/shared/brand-mark';
import { isMediaContent } from './chat-media';
import { publicChatKey, usePublicChatState } from './chats-queries';
import { usePublicRateChat, usePublicSendMessage, usePublicStartChat } from './chats-mutations';
import { Stars, errorMessage, formatHourMinute } from './chats-format';

const TOKEN_KEY = 'erp.chat.token';

/** Seletor de tenant da página (/chat?tenant=<slug>) — mesmo contrato que a
 *  API pública aceita como `?tenant=` no start e no config. */
function pageTenant(): string | undefined {
  return new URLSearchParams(window.location.search).get('tenant')?.trim() || undefined;
}

/** 400 (tenant_required etc.) é erro de integração, não de rede: um warn único
 *  no console para o dev perceber que a página foi aberta sem ?tenant=. */
let warnedConfigError = false;
function warnConfigErrorOnce(error: ApiError) {
  if (warnedConfigError || error.status !== 400) return;
  warnedConfigError = true;
  console.warn(
    `[chat-widget] GET /public/chat/config falhou (${error.error}) — abra a página como /chat?tenant=<slug>.`
  );
}

/** Config pública do widget (v11) — best-effort: erro ⇒ null (widget segue). */
function publicWidgetConfigQuery(tenant: string | undefined) {
  return queryOptions({
    queryKey: ['erp', 'public-chat', 'config', tenant ?? null] as const,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      const res = await api<PublicWidgetConfig>('/public/chat/config', {
        query: { tenant },
        signal,
      });
      if (res.error) {
        warnConfigErrorOnce(res.error);
        return null;
      }
      return res.data;
    },
  });
}

export function PublicChatPage() {
  const qc = useQueryClient();
  const tenant = pageTenant();
  const configQ = useQuery(publicWidgetConfigQuery(tenant));
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));

  const start = (next: string) => {
    localStorage.setItem(TOKEN_KEY, next);
    setToken(next);
  };

  const reset = () => {
    if (token) qc.removeQueries({ queryKey: publicChatKey(token) });
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const config = configQ.data ?? null;
  const disabled = config !== null && !config.enabled;

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-6">
      <div className="flex h-[min(40rem,calc(100dvh-3rem))] w-full max-w-md flex-col overflow-hidden rounded-lg border bg-card shadow-lg">
        {/* Token salvo vem PRIMEIRO: desligar o widget bloqueia só o início de
            novas conversas — quem já está em atendimento segue até o fim. */}
        {token ? (
          <WidgetSession token={token} config={config} onReset={reset} />
        ) : disabled ? (
          <WidgetDisabled config={config} />
        ) : (
          <WidgetStart config={config} tenant={tenant} onStarted={start} />
        )}
      </div>
    </div>
  );
}

/**
 * Header petróleo do widget: marca do tenant (logo e nome, spec 017) + título
 * ("Atendimento", ou o configurado em /settings/chat) + subtítulo.
 */
function WidgetHeader({
  subtitle,
  config,
}: {
  subtitle?: ReactNode;
  config: PublicWidgetConfig | null;
}) {
  return (
    <header className="shrink-0 bg-sidebar px-4 py-3 text-white">
      <span className="flex items-center gap-1.5 text-sm font-semibold">
        <PublicBrandMark className="text-[15px]" iconClassName="size-4" logoClassName="size-5" />
        <span aria-hidden className="text-white/50">
          —
        </span>
        {config?.title?.trim() || 'Atendimento'}
      </span>
      {subtitle ? <p className="mt-0.5 text-xs text-white/70">{subtitle}</p> : null}
    </header>
  );
}

/** Chat desligado em /settings/chat: sem formulário, sem start. */
function WidgetDisabled({ config }: { config: PublicWidgetConfig | null }) {
  return (
    <>
      <WidgetHeader config={config} />
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
        <MessageCircleOff className="size-8 text-muted-foreground/50" strokeWidth={1.5} />
        <p className="text-sm font-medium">Atendimento por chat indisponível</p>
        <p className="text-sm text-muted-foreground">
          O chat está temporariamente desativado. Tente novamente mais tarde.
        </p>
      </div>
    </>
  );
}

// ── Formulário inicial (sem token) ──────────────────────────────────────────

function WidgetStart({
  config,
  tenant,
  onStarted,
}: {
  config: PublicWidgetConfig | null;
  tenant: string | undefined;
  onStarted: (token: string) => void;
}) {
  const startM = usePublicStartChat(tenant);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    if (!name.trim()) {
      setError('Informe seu nome para iniciar.');
      return;
    }
    startM.mutate(
      { name: name.trim(), phone: phone.trim() || undefined },
      {
        onSuccess: ({ token }) => onStarted(token),
        onError: (e) => setError(errorMessage(e)),
      }
    );
  };

  return (
    <>
      <WidgetHeader subtitle="Fale com a nossa equipe" config={config} />
      <form
        className="flex flex-1 flex-col justify-center gap-3 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-sm text-muted-foreground">
          {/* A saudação configurada chega como mensagem de sistema na thread
              (entrega única) — aqui fica só a instrução do formulário. */}
          Informe seus dados para iniciar uma conversa com um de nossos atendentes.
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pc-name" className="text-[13px] font-semibold">
            Nome<span className="-ml-1 text-destructive">*</span>
          </Label>
          <Input
            id="pc-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Seu nome"
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pc-phone" className="text-[13px] font-semibold">
            Telefone
          </Label>
          <Input
            id="pc-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(00) 00000-0000"
          />
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <Button type="submit" className="mt-1 h-10 w-full" disabled={startM.isPending}>
          {startM.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
          Iniciar conversa
        </Button>
      </form>
    </>
  );
}

// ── Sessão ativa (com token) ────────────────────────────────────────────────

function WidgetSession({
  token,
  config,
  onReset,
}: {
  token: string;
  config: PublicWidgetConfig | null;
  onReset: () => void;
}) {
  const stateQ = usePublicChatState(token);
  const state = stateQ.data;

  // Token inválido/expirado (chat apagado etc.) → limpa e volta ao formulário.
  useEffect(() => {
    const status = (stateQ.error as unknown as ApiError | null)?.status;
    if (status === 404 || status === 410) onReset();
  }, [stateQ.error, onReset]);

  const messages = state?.messages ?? [];
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const archived = state?.status === 'archived';
  const subtitle = !state
    ? undefined
    : archived
      ? undefined
      : state.responsibleName
        ? `Você está falando com ${state.responsibleName}`
        : 'Aguardando um atendente…';

  return (
    <>
      <WidgetHeader subtitle={subtitle} config={config} />

      <div ref={threadRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-background px-3 py-3">
        {stateQ.isPending ? (
          <div className="flex h-full items-center justify-center">
            <Spinner className="size-5" />
          </div>
        ) : stateQ.isError && !state ? (
          <p className="p-4 text-center text-sm text-muted-foreground">
            Não foi possível carregar a conversa. ({errorMessage(stateQ.error)})
          </p>
        ) : messages.length === 0 ? (
          <p className="p-4 text-center text-sm text-muted-foreground">
            Envie uma mensagem para começar.
          </p>
        ) : (
          messages.map((m) => <WidgetBubble key={m.id} message={m} />)
        )}
      </div>

      {state && archived ? (
        <ArchivedFooter state={state} token={token} onReset={onReset} />
      ) : (
        <WidgetComposer token={token} disabled={!state} />
      )}
    </>
  );
}

/** Perspectiva do cliente: minhas mensagens (inbound) à direita em teal. */
function WidgetBubble({ message }: { message: ChatMessage }) {
  if (message.authorType === 'system') {
    return <div className="py-0.5 text-center text-xs text-muted-foreground">{message.text}</div>;
  }
  const mine = message.direction === 'inbound';
  return (
    <div className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2',
          mine
            ? 'rounded-br-sm bg-primary text-primary-foreground'
            : 'rounded-bl-sm border bg-card text-foreground'
        )}
      >
        {!mine && message.authorName ? (
          <div className="text-[11px] font-semibold text-muted-foreground">{message.authorName}</div>
        ) : null}
        {isMediaContent(message) ? (
          <WidgetMedia message={message} />
        ) : (
          <div className="text-sm break-words whitespace-pre-wrap">{message.text}</div>
        )}
        <div
          className={cn(
            'mt-0.5 text-right text-[11px] tabular-nums',
            mine ? 'text-primary-foreground/70' : 'text-muted-foreground'
          )}
        >
          {formatHourMinute(message.createdAt)}
        </div>
      </div>
    </div>
  );
}

/**
 * Mídia no widget PÚBLICO (v8): o mediaUrl é a rota token-authed
 * (`/public/chat/:token/messages/:mid/media` — o token vai no path, sem
 * header), então <img src>/<a href> funcionam direto (o browser segue o 302
 * da presignada). Imagem inline; demais tipos como link de download — widget
 * leve, sem fetch/blob.
 */
function WidgetMedia({ message }: { message: ChatMessage }) {
  if (!message.mediaUrl) {
    // Backfill em andamento (ou que falhou): fallback textual da mensagem
    // (caption ?? filename ?? '[imagem]' etc., já em `text`) + dica — se o
    // media_pull falhar em definitivo, a bolha continua legível.
    return (
      <div className="space-y-0.5">
        {message.text ? (
          <div className="text-sm break-words whitespace-pre-wrap">{message.text}</div>
        ) : null}
        <div className="text-xs italic opacity-70">baixando mídia…</div>
      </div>
    );
  }
  const src = apiUrl(message.mediaUrl);
  const isImage = message.contentType === 'image' || message.contentType === 'sticker';
  return (
    <div className="space-y-1">
      {isImage ? (
        <a href={src} target="_blank" rel="noopener noreferrer">
          <img
            src={src}
            alt={message.caption ?? 'Imagem'}
            loading="lazy"
            className="max-h-56 max-w-full rounded-md object-contain"
          />
        </a>
      ) : (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-sm underline underline-offset-2 hover:opacity-80"
        >
          <Paperclip className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{message.mediaFilename ?? 'Baixar arquivo'}</span>
        </a>
      )}
      {message.caption ? (
        <div className="text-sm break-words whitespace-pre-wrap">{message.caption}</div>
      ) : null}
    </div>
  );
}

function WidgetComposer({ token, disabled }: { token: string; disabled?: boolean }) {
  const send = usePublicSendMessage(token);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const value = text.trim();
    if (!value || send.isPending) return;
    setError(null);
    send.mutate(value, {
      onSuccess: () => setText(''),
      onError: (e) => setError(errorMessage(e)),
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="shrink-0 border-t p-3">
      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={disabled}
          placeholder="Escreva sua mensagem…"
          aria-label="Mensagem"
          className="field-sizing-content max-h-24 min-h-9 w-full flex-1 resize-none rounded-md border border-input bg-card px-3 py-2 text-sm shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <Button
          size="icon-sm"
          aria-label="Enviar mensagem"
          onClick={submit}
          disabled={disabled || send.isPending || text.trim() === ''}
        >
          {send.isPending ? (
            <Spinner className="size-4 border-primary-foreground" />
          ) : (
            <SendHorizonal />
          )}
        </Button>
      </div>
      {error ? (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Encerrado: banner + avaliação 1–5 (quando não cancelado) + nova conversa. */
function ArchivedFooter({
  state,
  token,
  onReset,
}: {
  state: PublicChatState;
  token: string;
  onReset: () => void;
}) {
  const rate = usePublicRateChat(token);
  const [rated, setRated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showThanks = rated || state.assessment !== null;
  const canRate = !state.canceled && !showThanks;

  return (
    <div className="shrink-0 space-y-3 border-t bg-muted px-4 py-4 text-center">
      <p className="text-sm font-medium text-foreground">
        {state.canceled ? 'Conversa cancelada' : 'Conversa encerrada'}
      </p>

      {canRate ? (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">Como você avalia este atendimento?</p>
          <Stars
            value={null}
            disabled={rate.isPending}
            onRate={(assessment) =>
              rate.mutate(assessment, {
                onSuccess: () => setRated(true),
                onError: (e) => setError(errorMessage(e)),
              })
            }
            className="justify-center"
          />
        </div>
      ) : null}

      {showThanks ? (
        <div className="space-y-1.5">
          {state.assessment !== null ? (
            <Stars value={state.assessment} className="justify-center" />
          ) : null}
          <p className="text-xs text-muted-foreground">Obrigado pela avaliação!</p>
        </div>
      ) : null}

      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <Button variant="outline" size="sm" onClick={onReset}>
        Nova conversa
      </Button>
    </div>
  );
}
